import { getLocalStorageKeysById, ids } from '@/config/pinia'
import router from '@/router'
import { User, UserRole } from '@/types/user'
import { AuthApi, type CurrentUser } from '@/api/auth'
import { defineStore } from 'pinia'

/**
 * 用户状态管理
 * 
 * 功能包括：
 * - 用户信息存储和管理
 * - JWT Token管理
 * - 权限验证
 * - 登录/登出处理
 * - 密码状态管理
 */
export default defineStore(
  ids.user,
  () => {
    // 用户信息
    const info = ref(new User())

    // JWT token
    const token = ref<string | null>(null)

    // 密码状态
    const passwordHashIsEmpty = ref<boolean>(false)

    // 是否已登录
    const isLoggedIn = computed(() => !!token.value)

    // 是否需要设置密码
    const needsPasswordSetup = computed(() => passwordHashIsEmpty.value)

    // 是否可以访问管理页面 (登录且密码已设置)
    const canAccessAdminPages = computed(() => isLoggedIn.value && !needsPasswordSetup.value)

    // 是否为管理员（admin或super）
    const isAdmin = computed(() => 
      info.value.role === UserRole.ADMIN || info.value.role === UserRole.SUPER
    )

    // 是否为超级管理员
    const isSuper = computed(() => info.value.role === UserRole.SUPER)

    // 是否为普通用户
    const isUser = computed(() => info.value.role === UserRole.USER)

    /**
     * 检查用户是否有指定权限
     * @param requiredRole 所需角色
     * @returns boolean 是否有权限
     */
    const hasRole = (requiredRole: UserRole): boolean => {
      const roleHierarchy: Record<UserRole, number> = {
        [UserRole.USER]: 1,
        [UserRole.ADMIN]: 2, 
        [UserRole.SUPER]: 3,
      }
      
      const userLevel = roleHierarchy[info.value.role] || 0
      const requiredLevel = roleHierarchy[requiredRole] || 0
      
      return userLevel >= requiredLevel
    }

    /**
     * 设置用户信息
     * @param userData 用户数据
     */
    const setUserInfo = (userData: Partial<CurrentUser>) => {
      Object.assign(info.value, userData)
    }

    /**
     * 设置密码状态
     * @param isEmpty 密码是否为空
     */
    const setPasswordHashIsEmpty = (isEmpty: boolean) => {
      passwordHashIsEmpty.value = isEmpty
    }

    /**
     * 设置Token
     * @param newToken JWT token
     */
    const setToken = (newToken: string) => {
      token.value = newToken
    }

    /**
     * 清除用户状态
     */
    const clearUserState = () => {
      info.value = new User()
      token.value = null
      passwordHashIsEmpty.value = false
    }

    /**
     * 刷新用户信息
     */
    const refreshUserInfo = async (): Promise<void> => {
      if (!token.value) return
      
      try {
        const userData = await AuthApi.getCurrentUser()
        setUserInfo(userData)
      } catch (error) {
        console.error('刷新用户信息失败:', error)
        // 如果获取用户信息失败，可能token已失效，清除状态
        clearUserState()
        throw error
      }
    }

    /**
     * 登出处理
     */
    const loginOut = () => {
      // 清除本地存储
      localStorage.removeItem(getLocalStorageKeysById(ids.user))
      
      // 清除状态
      clearUserState()
      
      ElNotification.success('您已成功退出登录')
      
      // 跳转到登录页
      router.push({ name: 'Login' })
    }

    /**
     * 验证Token有效性 (简单检查是否存在)
     */
    const verifyToken = async (): Promise<boolean> => {
      return !!token.value
    }

    return {
      // 状态
      info,
      token,
      passwordHashIsEmpty,
      
      // 计算属性
      isLoggedIn,
      needsPasswordSetup,
      canAccessAdminPages,
      isAdmin,
      isSuper,
      isUser,
      
      // 方法
      hasRole,
      setUserInfo,
      setPasswordHashIsEmpty,
      setToken,
      clearUserState,
      refreshUserInfo,
      loginOut,
      verifyToken,
    }
  },
  {
    persist: true,
  },
)
